/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";
import { SigninPopupCallback, SilentSigninCallback } from "components/oidc/callback";
import { RootLayout } from "layouts/root-layout/RootLayout";
import { RootErrorBoundary } from "./rootErrorBoundary";
import { RequireAuth } from "components/oidc/requireAuth";
import { SandboxesView } from "views/sandboxes-view/SandboxesView";
import { SearchView } from "views/search-view/SearchView";
import { SandboxView } from "views/sandbox/SandboxView";
import { runtimeEnv } from "configuration";
import { SandboxNotFoundView } from "views/sandbox-not-found/SandboxNotFoundView";
import { FeaturedSandboxesView } from "views/featured-sandboxes-view/FeaturedSandboxesView";
import { SandboxEmbedView } from "views/sandbox-embed-view/SandboxEmbedView";
import { lazy, Suspense } from "react";
import { SandboxImportView } from "views/sandbox-import-view/SandboxImportView";

const routeDefinition: RouteObject[] = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        element: <FeaturedSandboxesView />
      },
      {
        path: "sandboxes",
        element: <RequireAuth><SandboxesView /></RequireAuth>,
      },
      {
        path: "search",
        element: <SearchView />,
      },
      {
        path: "embed/:userName/:sandboxName",
        element: <SandboxEmbedView />,
      },
      {
        path: ":userName/:sandboxName",
        element: <SandboxView />,
      },
      {
        path: "import",
        element: <RequireAuth><SandboxImportView /></RequireAuth>
      },
      {
        path: "sandbox-not-found",
        element: <SandboxNotFoundView />
      }
    ],
  },
  {
    path: "/signin-oidc",
    element: <SigninPopupCallback />,
  },
  {
    path: "/silent-signin",
    element: <SilentSigninCallback />,
  },
  {
    path: "/signout-callback-oidc",
    element: <Navigate to={"/"} />,
  },
];

// Dynamically adding samples route only for preview in localhost
if (process.env.NODE_ENV === "development") {
  const SamplesView = lazy(() => import("views/samples-view/SamplesView"));

  routeDefinition[0].children?.push({
    path: "samples",
    element: <Suspense fallback={<></>}>
      <SamplesView />
    </Suspense>
  });
}

export const router = createBrowserRouter(routeDefinition, {
  basename: runtimeEnv.REACT_APP_BASEURL,
});
